import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

const ROUTE_SECTION_MAP: Record<string, string> = {
  management:               'configurations',
  devices:                  'device-management',
  users:                    'account-users',
  integrations:             'integrations',
  'content-playlist':       'content-placement',
  stations:                 'configurations',
  branding:                 'branding',
  'licence-manager':        'licenses',
  'automated-actions':      'automated-actions',
  'web-player':             'tvnow',
  'tags':                   'tag-manager',
  'extended-program-guide': 'getting-started',
};

export interface HelpContext {
  section: string;
  subsection?: string;
}

@Injectable({ providedIn: 'root' })
export class HelpService {
  private stack: HelpContext[] = [];
  drawerOpen = false;

  constructor(private router: Router) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.stack = [];
        this.setDrawerOpen(false);
      }
    });
  }

  setDrawerOpen(open: boolean, panelSelector = '.content-library-dialog'): void {
    this.drawerOpen = open;
    if (open) {
      // Wait for the 0.5s slide-in animation to finish, then read the panel's
      // actual left edge and store it as a CSS variable the help button uses.
      setTimeout(() => {
        const drawer = document.querySelector(panelSelector) as HTMLElement;
        if (drawer) {
          const left = drawer.getBoundingClientRect().left;
          const right = window.innerWidth - left + 12;
          document.documentElement.style.setProperty('--help-btn-drawer-right', `${right}px`);
        }
      }, 600);
    } else {
      document.documentElement.style.removeProperty('--help-btn-drawer-right');
    }
  }

  /** Push a context onto the stack. Returns a cleanup function that pops it. */
  pushContext(section: string, subsection?: string): () => void {
    const ctx: HelpContext = { section, subsection };
    this.stack.push(ctx);
    return () => {
      const idx = this.stack.lastIndexOf(ctx);
      if (idx >= 0) this.stack.splice(idx, 1);
    };
  }

  /** Replace the subsection of the top context (used when tabs change). */
  updateTopContext(section: string, subsection?: string): void {
    if (this.stack.length > 0) {
      this.stack[this.stack.length - 1] = { section, subsection };
    } else {
      this.stack.push({ section, subsection });
    }
  }

  getContext(): HelpContext {
    if (this.stack.length > 0) {
      return this.stack[this.stack.length - 1];
    }
    const segments = this.router.url.split('/').filter(Boolean);
    const raw = segments[1] ?? segments[0] ?? '';
    const routeSegment = raw.split('?')[0].split('#')[0];
    return { section: ROUTE_SECTION_MAP[routeSegment] ?? '' };
  }

  openHelp(): void {
    const { section, subsection } = this.getContext();
    const hash = subsection
      ? `#${section}--${subsection}`
      : section
      ? `#${section}`
      : '';
    window.open(`/user_guide/index.html${hash}`, '_blank', 'noopener,noreferrer');
  }
}
